
import { useState, useEffect } from 'react';
import { FoodRecipe, RecipeIngredient } from '@/services/recipes/types';
import { getRecipeIngredients } from '@/services/recipes/recipeService';
import { hasConsumedRecipeToday, markRecipeAsConsumed } from '@/services/consumption/consumptionService';
import { getSelectedProfileId } from '@/utils/geolocation';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

// Función auxiliar para procesar los datos de la receta
const processRecipeData = (recipeData: any): FoodRecipe => {
  // Función de ayuda para procesar campos de beneficios
  const processBenefitsField = (field: string | string[] | null): string[] => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    // Si es un string, intentamos parsearlo como JSON
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [field];
    } catch (e) {
      // Si no es un JSON válido, lo tratamos como un solo string
      return [field];
    }
  };

  return {
    ...recipeData,
    benefits: processBenefitsField(recipeData.benefits),
    benefits_en: processBenefitsField(recipeData.benefits_en),
    benefits_es: processBenefitsField(recipeData.benefits_es),
    benefits_pt: processBenefitsField(recipeData.benefits_pt),
  };
};

export const useRecipeDetail = (recipeId: string | undefined) => {
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState<FoodRecipe | null>(null);
  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [consumed, setConsumed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const profileId = getSelectedProfileId();

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!recipeId) {
        console.error('Recipe ID is undefined');
        setError('ID de receta no proporcionado');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching recipe with ID:', recipeId);
        
        // Obtener datos de la receta
        const { data, error } = await supabase
          .from('food_recipes')
          .select('*')
          .eq('id', recipeId)
          .maybeSingle();
          
        if (error) {
          console.error('Error fetching recipe:', error);
          setError('Error al cargar la receta: ' + error.message);
          throw error;
        }
        
        if (!data) {
          console.error('Recipe not found with ID:', recipeId);
          setError('Receta no encontrada');
          return;
        }
        
        console.log('Recipe data loaded:', data);
        // Procesar los datos de la receta antes de almacenarlos en el estado
        setRecipe(processRecipeData(data));
        
        // Obtener ingredientes de la receta
        try {
          const ingredientsData = await getRecipeIngredients(recipeId);
          console.log('Recipe ingredients loaded:', ingredientsData);
          setIngredients(ingredientsData);
        } catch (ingredientsError) {
          console.error('Error loading recipe ingredients:', ingredientsError);
          // Continue with the recipe even if ingredients fail to load
        }
        
        // Verificar si el usuario ya consumió esta receta hoy
        if (profileId) {
          try {
            const alreadyConsumed = await hasConsumedRecipeToday(profileId, recipeId);
            setConsumed(alreadyConsumed);
          } catch (consumedError) {
            console.error('Error checking recipe consumption:', consumedError);
            // Continue even if consumption check fails
          }
        }
      } catch (error: any) {
        console.error('Error loading recipe details:', error);
        setError(error?.message || 'Error al cargar la receta');
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecipe();
  }, [recipeId, navigate, profileId]);

  const handleConsume = async () => {
    if (!recipeId || !profileId) {
      toast.error('Debes seleccionar un perfil para registrar esta comida');
      return;
    }
    
    try {
      const success = await markRecipeAsConsumed(profileId, recipeId);
      if (success) {
        setConsumed(true);
        toast.success('¡Has registrado esta comida correctamente!');
      }
    } catch (error) {
      console.error('Error marking recipe as consumed:', error);
      toast.error('Error al registrar el consumo');
    }
  };

  return {
    recipe,
    ingredients,
    loading,
    consumed,
    error,
    handleConsume
  };
};
